/**
 * Horizontalize — geometry for the live preview.
 *
 * The server draws the saved image (backend/image_horizontalize.py);
 * the dialog previews it with plain CSS. These functions are the same
 * maths as the Python module, with the same test cases on both sides
 * (frontend/scripts/horizontalize.test.mjs,
 * backend/tests/test_image_horizontalize.py), so the preview and the
 * saved image agree.
 *
 * Rects are `{x, y, w, h}` in CANVAS pixels (1300 x 800 by default).
 * The photo on top always keeps its own shape: x, y and w are free, h
 * follows.
 *
 * Pure and framework-free.
 */

export const DEFAULT_W = 1300;
export const DEFAULT_H = 800;
export const MIN_SIDE = 400;
export const MAX_SIDE = 2400;
export const BG_OVERSCAN = 1.1;
export const BLUR_SIGMA_RATIO = 0.022;
export const GLOW_SIGMA_RATIO = 0.016;
export const GLOW_OPACITY = 0.45;
export const MIN_FG_WIDTH_RATIO = 0.08;
export const MAX_FG_WIDTH_RATIO = 6.0;
export const MIN_VISIBLE_RATIO = 0.1;

const clampNum = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function clampCanvas(w, h) {
  const one = (v, def) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) && v !== "" && v !== null ? clampNum(n, MIN_SIDE, MAX_SIDE) : def;
  };
  return { w: one(w, DEFAULT_W), h: one(h, DEFAULT_H) };
}

/** Background: the photo filling the whole canvas, centred, overscanned. */
export function coverRect(iw, ih, cw, ch, overscan = BG_OVERSCAN) {
  const scale = Math.max(cw / iw, ch / ih) * overscan;
  const w = iw * scale;
  const h = ih * scale;
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h };
}

/** Foreground start: the whole photo, as large as fits, centred. */
export function defaultFgRect(iw, ih, cw, ch) {
  const scale = Math.min(cw / iw, ch / ih);
  const w = iw * scale;
  const h = ih * scale;
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h };
}

/** A foreground rect that is in bounds and has the photo's shape. */
export function cleanFgRect(rect, iw, ih, cw, ch) {
  const x0 = Number(rect?.x);
  const y0 = Number(rect?.y);
  const w0 = Number(rect?.w);
  if (![x0, y0, w0].every(Number.isFinite) || w0 <= 0) {
    return defaultFgRect(iw, ih, cw, ch);
  }
  const w = clampNum(w0, cw * MIN_FG_WIDTH_RATIO, cw * MAX_FG_WIDTH_RATIO);
  const h = (w * ih) / iw;
  const keepX = cw * MIN_VISIBLE_RATIO;
  const keepY = ch * MIN_VISIBLE_RATIO;
  return {
    x: clampNum(x0, keepX - w, cw - keepX),
    y: clampNum(y0, keepY - h, ch - keepY),
    w,
    h,
  };
}

/** Drag: move by (dx, dy) canvas pixels. */
export function moveRect(rect, dx, dy, iw, ih, cw, ch) {
  return cleanFgRect({ x: rect.x + dx, y: rect.y + dy, w: rect.w }, iw, ih, cw, ch);
}

/**
 * Centre the photo on the canvas along one axis — "x" (horizontally),
 * "y" (vertically) or "both" — keeping its size and the other axis.
 */
export function centerRect(rect, axis, iw, ih, cw, ch) {
  const h = (rect.w * ih) / iw;
  const x = axis === "x" || axis === "both" ? (cw - rect.w) / 2 : rect.x;
  const y = axis === "y" || axis === "both" ? (ch - h) / 2 : rect.y;
  return cleanFgRect({ x, y, w: rect.w }, iw, ih, cw, ch);
}

/** Is the photo already centred along `axis` (within half a pixel)? */
export function isCentered(rect, axis, cw, ch) {
  const offX = Math.abs(rect.x + rect.w / 2 - cw / 2);
  const offY = Math.abs(rect.y + rect.h / 2 - ch / 2);
  if (axis === "x") return offX < 0.5;
  if (axis === "y") return offY < 0.5;
  return offX < 0.5 && offY < 0.5;
}

/**
 * Resize to `newW`, keeping the point at (ax, ay) — fractions of the
 * rect, 0..1 — fixed on the canvas. (0.5, 0.5) zooms about the centre
 * (the slider); a corner handle anchors the opposite corner.
 */
export function resizeRect(rect, newW, ax, ay, iw, ih, cw, ch) {
  const w = clampNum(newW, cw * MIN_FG_WIDTH_RATIO, cw * MAX_FG_WIDTH_RATIO);
  const h = (w * ih) / iw;
  const px = rect.x + rect.w * ax;
  const py = rect.y + rect.h * ay;
  return cleanFgRect({ x: px - w * ax, y: py - h * ay, w }, iw, ih, cw, ch);
}

/** Slider position: the photo's width as a % of its "fit" width. */
export function zoomPercent(rect, iw, ih, cw, ch) {
  const fit = defaultFgRect(iw, ih, cw, ch).w;
  return Math.round((rect.w / fit) * 100);
}

export function rectForZoom(rect, percent, iw, ih, cw, ch) {
  const fit = defaultFgRect(iw, ih, cw, ch).w;
  return resizeRect(rect, (fit * Number(percent)) / 100, 0.5, 0.5, iw, ih, cw, ch);
}

/** Largest zoom % the limits allow for this photo and canvas. */
export function maxZoomPercent(iw, ih, cw, ch) {
  const fit = defaultFgRect(iw, ih, cw, ch).w;
  return Math.floor(((cw * MAX_FG_WIDTH_RATIO) / fit) * 100);
}

export function minZoomPercent(iw, ih, cw, ch) {
  const fit = defaultFgRect(iw, ih, cw, ch).w;
  return Math.ceil(((cw * MIN_FG_WIDTH_RATIO) / fit) * 100);
}

/**
 * CSS for the preview at `scale` (stage pixels per canvas pixel).
 * A CSS `blur(Npx)` takes the Gaussian sigma directly; a `box-shadow`
 * blur radius is twice the sigma.
 */
export function previewStyles(cw, scale) {
  return {
    blurPx: cw * BLUR_SIGMA_RATIO * scale,
    glowPx: cw * GLOW_SIGMA_RATIO * 2 * scale,
    glowColor: `rgba(0, 0, 0, ${GLOW_OPACITY})`,
  };
}

/** A canvas rect in stage pixels, as inline style. */
export function stageBox(rect, scale) {
  return {
    left: `${rect.x * scale}px`,
    top: `${rect.y * scale}px`,
    width: `${rect.w * scale}px`,
    height: `${rect.h * scale}px`,
  };
}

/** Is the article's current image a horizontalized one? */
export function isActive(article) {
  const meta = article?.image_horizontalize;
  return !!(
    meta &&
    meta.result_url &&
    meta.source_url &&
    meta.result_url === article.generated_image_url
  );
}

/** What the tool opens on: the original + last placement, or the current image. */
export function openingState(article) {
  if (isActive(article)) {
    const m = article.image_horizontalize;
    return {
      sourceUrl: m.source_url,
      canvas: clampCanvas(m.canvas_w, m.canvas_h),
      rect: { x: m.x, y: m.y, w: m.w },
      active: true,
    };
  }
  return {
    sourceUrl: article?.generated_image_url || "",
    canvas: { w: DEFAULT_W, h: DEFAULT_H },
    rect: null,
    active: false,
  };
}
